import { InputHTMLAttributes, ReactNode, useState } from "react";
import { Eye, EyeOff, Loader2, Phone, Wifi, WifiOff } from "lucide-react";
import { useRealtime } from "../realtime/RealtimeProvider";
import { PRIORITY } from "../lib/format";

export function PasswordInput(props: InputHTMLAttributes<HTMLInputElement> & { "data-testid"?: string }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input {...props} className="fp-input pr-12" type={show ? "text" : "password"} />
      <button type="button" onClick={() => setShow(!show)} aria-label={show ? "Hide password" : "Show password"}
        className="absolute inset-y-0 right-0 flex w-12 items-center justify-center text-slate-500 hover:text-[#036248]" data-testid={`${props["data-testid"] || "password"}-toggle`}>
        {show ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>
    </div>
  );
}

export function Logo({ light = false, size = 36 }: { light?: boolean; size?: number }) {
  return (
    <span className="inline-flex items-center gap-2" data-testid="brand-logo">
      <img src="/icon-192.png" alt="FASTPASS" width={size} height={size} className={`rounded-lg ${light ? "ring-1 ring-white/30" : ""}`} />
    </span>
  );
}

const STATUS_CLS: Record<string, string> = {
  requested: "bg-amber-100 text-amber-900 border-amber-200",
  assigned: "bg-yellow-50 text-yellow-900 border-yellow-300",
  accepted: "bg-emerald-50 text-emerald-900 border-emerald-200",
  en_route: "bg-emerald-50 text-emerald-900 border-emerald-200",
  at_pickup: "bg-emerald-100 text-emerald-900 border-emerald-300",
  in_transit: "bg-[#036248] text-white border-[#036248]",
  completed: "bg-slate-100 text-slate-700 border-slate-200",
  cancelled: "bg-amber-50 text-amber-900 border-amber-300",
  paid: "bg-emerald-50 text-emerald-900 border-emerald-200",
  pending: "bg-amber-50 text-amber-900 border-amber-200",
  failed: "bg-amber-50 text-amber-900 border-amber-300",
  active: "bg-emerald-50 text-emerald-900 border-emerald-200",
  verified: "bg-emerald-50 text-emerald-900 border-emerald-200",
  available: "bg-emerald-50 text-emerald-900 border-emerald-200",
  busy: "bg-amber-50 text-amber-900 border-amber-200",
  offline: "bg-slate-100 text-slate-600 border-slate-200",
  suspended: "bg-amber-50 text-amber-900 border-amber-300",
  rejected: "bg-amber-50 text-amber-900 border-amber-300",
};

export function StatusBadge({ status, label, testId }: { status: string; label?: string; testId?: string }) {
  return (
    <span data-testid={testId} className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATUS_CLS[status] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
      {label || status.replace(/_/g, " ")}
    </span>
  );
}

export function PriorityBadge({ p }: { p: number }) {
  const v = PRIORITY[p] || PRIORITY[3];
  return <span className={`rounded-md px-2 py-0.5 text-xs font-bold ${v.cls}`}>{v.label}</span>;
}

export function SimBadge({ show = false }: { show?: boolean }) {
  if (!show) return null;
  return (
    <span title="Simulated development resource — not a real operational unit"
      className="rounded border border-dashed border-slate-400 bg-slate-50 px-1.5 py-0.5 font-mono text-[10px] font-medium uppercase text-slate-600">
      Simulated
    </span>
  );
}

export function ConnectionPill() {
  const { status } = useRealtime();
  const live = status === "open";
  return (
    <span data-testid="websocket-status-pill" aria-live="polite"
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${live ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800"}`}>
      {live ? <Wifi size={13} /> : status === "connecting" ? <Loader2 size={13} className="animate-spin" /> : <WifiOff size={13} />}
      {live ? "Live" : status === "connecting" ? "Connecting" : "Reconnecting"}
    </span>
  );
}

export function PageHeader({ title, subtitle, action }: { title: string; subtitle?: string; action?: ReactNode }) {
  return (
    <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl" data-testid="page-title">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-slate-600">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function EmptyState({ title, body, testId }: { title: string; body?: string; testId?: string }) {
  return (
    <div data-testid={testId} className="rounded-2xl border border-dashed border-slate-300 p-6 text-center">
      <p className="font-semibold text-slate-700">{title}</p>
      {body && <p className="mt-1 text-sm text-slate-500">{body}</p>}
    </div>
  );
}

export function Call911({ compact = false, hotline = "911" }: { compact?: boolean; hotline?: string }) {
  return (
    <a href={`tel:${hotline}`} data-testid="call-911-link"
      className={`flex items-center gap-3 rounded-2xl border border-amber-300 bg-amber-50 text-amber-900 ${compact ? "px-3 py-2 text-sm" : "p-4"}`}>
      <Phone size={compact ? 16 : 20} className="shrink-0" />
      <span><strong>Life-threatening emergency?</strong> Call <strong>{hotline}</strong> immediately{compact ? "" : " — FASTPASS does not replace the national emergency hotline."}</span>
    </a>
  );
}

export function Spinner({ label = "Loading…" }: { label?: string }) {
  return <div className="flex items-center gap-2 p-4 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" />{label}</div>;
}

export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      {children}
      {hint && <span className="block text-xs text-slate-500">{hint}</span>}
    </label>
  );
}
